import {App as VueApp, computed, ref, watch} from 'vue'
import {find, toLower} from 'lodash'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {defineStore} from 'pinia'
import {AccountPickerOption} from '@/types/AccountPickerOption'
import {AccountType} from '@/types/AccountType'
import {useAccountsStoreKey} from '@/types/InjectionKeys'
import {useSubAccounts} from '@/subaccount/subAccount.service'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'

const controlAccounts = ['1500', '2400']

const isControlAccount = (accountNo: string) => {
    return controlAccounts.some(controlAccountNo => controlAccountNo === accountNo)
}

const useAccounts = defineStore('accounts', () => {
    const {suppliers, customers} = useSubAccounts()
    const accountsList = ref<AccountPickerOption[]>([])

    const accounts = computed(() => accountsList)

    const findAccountOptionFromKey = (key: string): AccountPickerOption | undefined => {
        return find(accountsList.value, {key})
    }

    const findAccountOptionFrom = (accountNo: string, subAccountUuid: string | undefined = undefined) => {
        if (subAccountUuid) {
            return accountsList.value.find(account => account.subAccountUuid === subAccountUuid)
        } else {
            return accountsList.value.find(account => account.accountNo === accountNo)
        }
    }

    const matchesSearch = (searchString: string, account: BookkeepingAccount) => {
        const lcSearch = searchString.toLowerCase()
        return account.accountNo.startsWith(lcSearch)
            || toLower(JSON.stringify(account.name)).includes(lcSearch)
            || toLower(JSON.stringify(account.description)).includes(lcSearch)
            || toLower(JSON.stringify(account.keywords)).includes(lcSearch)
    }

    const init = () => {
        const currentBookkeepingAccount = [...(bookkeepingAccounts.value || [])]
            .filter(account => account.active)
            .filter(bookKeepingAccount => !isControlAccount(bookKeepingAccount.accountNo))
            .map(account => {
                return {
                    accountNo: account.accountNo,
                    name: account.name,
                    description: account.description,
                    key: account.accountNo,
                    accountType: AccountType.BOOKKEEPING,
                    icon: 'i-ph-book-fill',
                    vatSuggestionId: account.vatSuggestionId,
                    matchesSearch: (search: string) => matchesSearch(search, account)
                }
            })

        const filterActiveSubAccounts = (subAccounts: SubAccount[]) => {
            return subAccounts.filter(subAccount => subAccount.active)
        }

        const subAccounts = [
            ...filterActiveSubAccounts(suppliers.value),
            ...filterActiveSubAccounts(customers.value)
        ].map(subAccount => {
            const isCustomer = subAccount.relationType === CUSTOMER
            const masterAccountNo = isCustomer ? '1500' : '2400'
            const searchableBlob = JSON.stringify([
                subAccount.name,
                subAccount.address?.postalCode,
                subAccount.address?.postalLocation,
                subAccount.address?.addressLine,
                subAccount.contactInfo?.email,
                subAccount.notes
            ]).toLowerCase()

            return {
                accountNo: masterAccountNo,
                subAccountNo: subAccount.subAccountNo,
                subAccountUuid: subAccount.uuid,
                key: `${masterAccountNo}:${subAccount.subAccountNo}`,
                name: subAccount.name,
                description: `${subAccount.address?.addressLine}, ${subAccount.address?.postalCode} ${subAccount.address?.postalLocation}`,
                accountType: isCustomer ? AccountType.CUSTOMER : AccountType.SUPPLIER,
                icon: isCustomer ? 'i-mdi-account-circle' : 'i-mdi-factory',
                matchesSearch: (search: string) => {
                    const lcSearch = search.toLowerCase()
                    return masterAccountNo.startsWith(lcSearch)
                        || subAccount.subAccountNo.toString().startsWith(lcSearch)
                        || searchableBlob.includes(lcSearch)
                        || subAccount.orgNo === lcSearch
                        || subAccount.contactInfo?.phoneNumber === lcSearch
                }
            }
        })

        accountsList.value = [...subAccounts, ...currentBookkeepingAccount]
    }

    watch([suppliers, customers, bookkeepingAccounts], () => {
        init()
    })

    init()

    return {accounts, findAccountOptionFromKey, findAccountOptionFrom}
})

const plugin = {
    install(app: VueApp) {
        app.provide(useAccountsStoreKey, useAccounts)
    }
}

export {
    useAccounts,
    plugin
}